import { Injectable } from '@angular/core';

import {
  BehaviorSubject,
  Observable,
  asyncScheduler,
  combineLatest,
  forkJoin,
  of,
} from 'rxjs';
import {
  debounceTime,
  filter,
  map,
  shareReplay,
  startWith,
  switchMap,
  tap,
} from 'rxjs/operators';

import {
  Entity,
  ReactiveListVM,
  ReactiveStore,
  StatusState,
} from '@dg/shared-rsm';
import { AuthService, WindowLayoutService } from '@dg/shared-services';

import { TagsService } from '@app/tags/services/tags.service';
import { InputsService } from '@app/inputs/services/inputs.service';
import { AuthUser } from '@app/account/account-api.model';

import { Profile } from '@app/profile/profile.model';
import { ProfileService } from '@app/profile/services/profile.service';

import { TrackerService } from '@app/shared/services/tracker.service';
import { TrackingEventArgs } from '@app/shared/services/tracking.model';
import { Breadcrumb } from '@app/shared/components/breadcrumbs/breadcrumbs.component.model';
import { getEmptyTileCount } from '@app/profile/components/profile-overview-v2/profile-overview.utils';

import {
  ProfileSkillSignalsAPI,
  UserSkills,
} from '@app/profile/components/profile-skill-signals-page-v2/profile-skill-signals.model';

import {
  ExperienceItem,
  ProfileExperiencesFacade,
} from '@app/profile/components/profile-overview/experiences-v2/profile-experience.facade';
import {
  AchievementItem,
  ProfileAchievementsFacade,
} from '@app/profile/components/profile-overview/achievements-v2/achievements.facade';
import {
  LearningItem,
  ProfileLearningFacade,
} from '@app/profile/components/profile-overview/learning-v2/profile-learning.facade';
import {
  ProfileSkillSignalsPageState,
  ProfileSkillSignalsPageStoreState,
  ProfileSkillSignalsPageViewModel,
} from '@app/profile/components/profile-skill-signals-page-v2/profile-skill-signals.model';
import { EvaluationService } from '@app/tags/services/evaluation.service';
import { CheckpointService } from '@app/tags/services/checkpoint.service';
import { ProfileSkillSignalServiceV2 } from '@app/profile/components/profile-skill-signals-page-v2/profile-skill-signal-page.service';
import { TagsApi } from '@app/tags/tag-api.model';

const ALL_PAGE_DISPLAY_LIMIT = 8;
const LEARNINGS_PAGE_DISPLAY_LIMIT = 24;
const ACHIEVEMENTS_PAGE_DISPLAY_LIMIT = 12;
const EXPERIENCES_DISPLAY_LIMIT = 5;

// *****************************************************************
// Utility functions
// *****************************************************************

const initStore = (): ProfileSkillSignalsPageStoreState => ({
  tag: null,
  signalCount: { tagId: null, count: 0 },
  signalBreakdown: [],
  chartSignals: { signalCounts: [], signalHistories: [], xAxisLabels: [] },
  availableRatings: [],
  ratingDetails: [],
  isTagEvaluable: false,
  allCheckpoints: [], // fix
  isLoading: false,
  showSkeleton: false,
  signalType: 'All',
});

/**
 * Facade for the Profile Skill Signals Page
 */
@Injectable()
export class ProfileSkillSignalsPageFacade {
  /**
   * Scheduler used for `debounceTime()` for the profile skill signals page
   * This allows tests to override with a virtual scheduler
   */
  public static debounceScheduler = asyncScheduler;

  private get owner(): Profile {
    return this.profileService.profileInput.profile;
  }
  private _reload$ = new BehaviorSubject<boolean>(true);
  private _tagId$ = new BehaviorSubject<number>(null);
  private _enabled = true;
  private _store: ReactiveStore<ProfileSkillSignalsPageStoreState, Entity>;

  public get authUser(): AuthUser {
    return this.authService?.authUser;
  }
  public get snapshot(): ProfileSkillSignalsPageStoreState {
    return this._store.snapshot;
  }

  public get tagId(): number {
    return this._tagId$.value;
  }

  public set tagId(value: number) {
    if (value !== this._tagId$.value) {
      // refresh any time the tag is set
      this._tagId$.next(value);
    }
  }

  /**
   * Special property to either force reload or IGNORE other triggers
   * Use to temporarily disable the facade when not needed
   */
  public get enabled(): boolean {
    return this._enabled;
  }
  public set enabled(value: boolean) {
    if (value === this._enabled) return;
    this._enabled = value;

    // Force trigger$ emission to refresh the profile VM
    if (!!value) this._reload$.next(true);
  }

  public status$: Observable<StatusState>;
  public vm$: Observable<ProfileSkillSignalsPageViewModel>;

  public readonly experiencesSearch$: Observable<
    ReactiveListVM<ExperienceItem>
  >;

  public readonly achievementsSearch$: Observable<
    ReactiveListVM<AchievementItem>
  >;

  public readonly learningsSearch$: Observable<ReactiveListVM<LearningItem>>;

  constructor(
    private api: ProfileSkillSignalServiceV2,
    private evalService: EvaluationService,
    private checkpointService: CheckpointService,
    private tagsService: TagsService,
    private inputsService: InputsService,
    private authService: AuthService,
    private profileService: ProfileService,
    private tracker: TrackerService,
    private experiencesFacade: ProfileExperiencesFacade,
    private achievementsFacade: ProfileAchievementsFacade,
    private learningsFacade: ProfileLearningFacade,
    private windowLayoutService: WindowLayoutService
  ) {
    this._store = new ReactiveStore<ProfileSkillSignalsPageStoreState, Entity>(
      'profileSkillSignalsPage',
      initStore
    );
    this.experiencesSearch$ = this.experiencesFacade.experienceItems.vm$;
    this.achievementsSearch$ = this.achievementsFacade.achievementItems.vm$;
    this.learningsSearch$ = this.learningsFacade.learningItems.vm$;

    // ViewModel is state + computed properties
    this.vm$ = combineLatest([
      this._store.state$,
      this.authService.authUser$,
    ]).pipe(
      map(this.addComputedProperties.bind(this)),
      map(this.addViewModelAPI.bind(this)),
      shareReplay<ProfileSkillSignalsPageViewModel>({
        refCount: false,
        bufferSize: 1,
      })
    );
    this.status$ = this._store.status$;

    // Configure a permanent connection to external triggers
    // NOTE: if triggers are NOT BehaviorSubjects, may need to `startWith()` to force an initial emission
    const triggers$ = combineLatest([
      this._reload$,
      this._tagId$,
      this.tagsService.userTagsModified.pipe(startWith({})),
      this.inputsService.inputModify.pipe(startWith({})),
    ]).pipe(
      filter(() => this.enabled),
      debounceTime(20, ProfileSkillSignalsPageFacade.debounceScheduler)
    );

    this.updateWhen(triggers$);
  }

  /**
   * Append more results to experiences
   */
  public loadExperiences(reset = false): void {
    this.experiencesFacade.getExperiences(
      this.tagId,
      reset,
      EXPERIENCES_DISPLAY_LIMIT
    );
  }

  /**
   * Append more results to achievements
   */
  public loadAchievements(reset = false): void {
    const tagName = this._store.snapshot.tag.name;
    this.achievementsFacade.getAchievements(
      tagName,
      reset,
      this.getInputItemDisplayLimit()
    );
  }

  /**
   * Append more results to learnings
   */
  public loadLearnings(reset = false): void {
    const tagName = this._store.snapshot.tag.name;
    this.learningsFacade.getLearnings(
      tagName,
      reset,
      this.getInputItemDisplayLimit()
    );
  }

  public setSignalType(signalType: string): void {
    const tag = this._store.useQuery((s) => s.tag);
    if (!tag) {
      return;
    }
    this._store.update((state) => ({
      ...state,
      signalType,
    }));

    if (tag) {
      // don't do this on initialisation as tag isn't available. This is required when switching from skill signals
      // to overview and back again
      switch (signalType) {
        case 'All':
          this.reloadItems();
          break;
        case 'Experiences':
          this.loadExperiences(true);
          break;
        case 'Achievements':
          this.loadAchievements(true);
          break;
        case 'Learnings':
          this.loadLearnings(true);
          break;
      }
    }
  }

  // ************************************************************
  // Private Methods
  // ************************************************************

  private reloadItems() {
    this.loadAchievements(true);
    this.loadExperiences(true);
    this.loadLearnings(true);
  }

  /**
   * Establishes a permanent connection to external triggers and updates the store
   * based on queries results of 'data' providers.
   *
   * NOTE: this 'permanent connection' means we need a way to temporarily ignore trigger emissions
   *       hence the use of `enabled` for the facade
   */
  private updateWhen(triggers$: Observable<unknown>): void {
    const onSaveToStore = ({
      tag,
      signalBreakdown,
      chartSignals,
      signalSummaries,
      availableRatings,
      ratingDetails,
      isTagEvaluable,
      allCheckpoints,
    }) => {
      const signalCount = signalSummaries.signalCounts.find(
        (count) => count.tagId === this.tagId
      ) || { count: 0, tagId: null };

      this._store.update((state) => ({
        ...state,
        tag,
        signalBreakdown,
        chartSignals,
        signalSummaries,
        availableRatings,
        ratingDetails,
        isTagEvaluable,
        allCheckpoints,
        signalCount,
      }));

      this._store.setLoading(false);
    };

    // !! do not cache the `unsubscribe()` as this is a permanent connection
    triggers$
      .pipe(
        tap(() => this._store.setLoading(true)),
        switchMap(() => this.api.getUserSkills(this.owner.userProfileKey)),
        switchMap(this.getSkillSignalPageSettings.bind(this))
      )
      .subscribe(onSaveToStore);
  }

  // ************************************************************
  // Private Remote data accessors
  // ************************************************************

  /**
   * Load list of skill signal page settings
   */
  private getSkillSignalPageSettings(skillsAndRatingsCount: UserSkills) {
    // @TODO: this is not ideal, the `tags/getTag` EP doesn't work for unuauthed users
    const { skills } = skillsAndRatingsCount;
    const allSkills = [...skills.other, ...skills.focus];
    const tag = allSkills.find((s) => s.tagId === this.tagId);

    let queries: any = {
      signalBreakdown: this.api.getSignalBreakdown(
        this.owner.userProfileKey,
        this.tagId.toString()
      ),
      chartSignals: this.api.getSignalSkillPage(
        this.owner.userProfileKey,
        this.tagId.toString()
      ),
      signalSummaries: this.api.getSignalCounts(this.owner.userProfileKey),
      experiences: this.experiencesFacade.getExperiences(
        this.tagId,
        true,
        EXPERIENCES_DISPLAY_LIMIT
      ),
      achievements: this.achievementsFacade.getAchievements(
        tag.name,
        true,
        this.getInputItemDisplayLimit()
      ),
      learnings: this.learningsFacade.getLearnings(
        tag.name,
        true,
        this.getInputItemDisplayLimit()
      ),
      tag: of(tag),
    };
    if (!!this.authUser) {
      queries = {
        ...queries,
        availableRatings: this.tagsService.getAvailableRatingTypes(this.tagId),
        ratingDetails: this.tagsService.getTagRatingDetails(
          this.owner.userProfileKey,
          this.tagId
        ),
        isTagEvaluable: this.evalService.getIsEvaluableByTagId(this.tagId),
        allCheckpoints: this.checkpointService.getAllCheckpoints(
          this.tagId,
          this.owner.userProfileKey
        ),
      };
    }
    this.trackProfileView(tag);
    return forkJoin(queries);
  }

  // *******************************************************
  // ViewModel 'injection' methods
  // *******************************************************

  /**
   * Inject the Facade computed properties into view model
   */
  private addComputedProperties([state, authUser, isLoading]: [
    ProfileSkillSignalsPageStoreState,
    AuthUser,
    boolean
  ]): ProfileSkillSignalsPageState {
    const showExperiences = !authUser?.hideOverviewExperience;
    const showAchievements = !authUser?.hideOverviewAchievement;
    const isLoggedIn = !!authUser;
    const ownerIsViewing = this.profileService.ownerIsViewing;
    const ownerKey = this.owner.id;

    let breadcrumb: Breadcrumb;

    const pageBreadcrumbs = [];
    breadcrumb = { label: this.owner.name, routerLink: '../overview' };
    pageBreadcrumbs.push(breadcrumb);

    if (state.tag?.name) {
      pageBreadcrumbs.push({ link: '', label: state.tag.name });
    }

    return {
      ...state,
      showExperiences,
      showAchievements,
      isLoggedIn,
      ownerIsViewing,
      ownerKey,
      pageBreadcrumbs,
      isMobile: this.windowLayoutService.isMobile,
      owner: this.owner,
      isPageLoading: state.isLoading || state.showSkeleton,
    };
  }

  /**
   * Inject the Facade API into view model
   */
  private addViewModelAPI(
    state: ProfileSkillSignalsPageViewModel
  ): ProfileSkillSignalsPageViewModel & ProfileSkillSignalsAPI {
    const api = {
      loadExperiences: this.loadExperiences.bind(this),
      loadAchievements: this.loadAchievements.bind(this),
      loadLearnings: this.loadLearnings.bind(this),
      getEmptyTileCount,
    };

    return {
      ...state,
      ...api,
    };
  }

  // ************************************************************
  // Event Reporting
  // ************************************************************
  private trackProfileView(tag: TagsApi.Tag): void {
    // only track on the initial load, or if a new profile is loaded
    if (!!tag) {
      this.trackEvent('Skill Signal Viewed', {
        SkillId: this.tagId,
        SkillName: tag?.name,
        ProfileOwnerId: this.owner.userProfileKey,
      });
    }
  }

  private trackEvent(
    action: string,
    properties?: { [name: string]: any }
  ): void {
    let data: TrackingEventArgs = { action };
    // add properties, if defined
    if (properties) {
      data = {
        ...data,
        properties,
      };
    }
    // track event data
    this.tracker.trackEventData(data);
  }

  private getInputItemDisplayLimit() {
    const signalType = this._store.useQuery((s) => s.signalType);
    switch (signalType) {
      case 'All':
        return ALL_PAGE_DISPLAY_LIMIT;
      case 'Experiences':
        return EXPERIENCES_DISPLAY_LIMIT;
      case 'Achievements':
        return ACHIEVEMENTS_PAGE_DISPLAY_LIMIT;
      case 'Learnings':
        return LEARNINGS_PAGE_DISPLAY_LIMIT;
      default:
        return ALL_PAGE_DISPLAY_LIMIT;
    }
  }
}
